import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  acceptInvitation,
  cancelInvitation,
  checkSlug,
  createAndSetActiveOrganization,
  getActiveMember,
  getFullOrganization,
  getInvitation,
  inviteMember,
  listOrganizations,
  rejectInvitation,
  removeMember,
  setActiveOrganization,
  updateMemberRole,
} from '.';

export const queryKeys = {
  organization: 'organization',
  organizations: 'organizations',
  checkSlug: 'checkSlug',
  invitation: 'invitation',
  activeMember: 'activeMember',
};

export const getOrganizationQueryOptions = queryOptions({
  queryKey: [queryKeys.organization],
  queryFn: getFullOrganization,
  staleTime: 60 * 1000,
});

export const getActiveMemberQueryOptions = queryOptions({
  queryKey: [queryKeys.activeMember],
  queryFn: getActiveMember,
  staleTime: 60 * 1000,
});

export const useListOrganizations = () => {
  return useQuery({
    queryKey: [queryKeys.organizations],
    queryFn: listOrganizations,
  });
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: createAndSetActiveOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organizations] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
      toast.success('Success', {
        description: 'Organization created successfully!',
      });
      navigate({ to: '/' });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useSetActiveOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setActiveOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
    },
  });
};

export const useInviteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
    },
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
    },
  });
};

export const useUpdateMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMemberRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
    },
  });
};

export const useCheckSlug = (slug: string) => {
  return useQuery({
    queryKey: [queryKeys.checkSlug, slug],
    queryFn: () => checkSlug(slug),
    enabled: slug.length >= 2 && /^[a-z0-9-]+$/.test(slug),
    retry: false,
    staleTime: 30 * 1000,
  });
};

export const useGetInvitation = (invitationId: string) => {
  return useQuery({
    queryKey: [queryKeys.invitation, invitationId],
    queryFn: () => getInvitation({ invitationId }),
    enabled: !!invitationId,
    retry: false,
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] });
      toast.success('Success', {
        description: 'Invitation accepted successfully!',
      });
      navigate({ to: '/projects' });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};

export const useRejectInvitation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: rejectInvitation,
    onSuccess: () => {
      toast.success('Success', {
        description: 'Invitation rejected successfully!',
      });
      navigate({ to: '/' });
    },
    onError: (error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
};
